import React, { useEffect, useState, useMemo } from 'react';
import { Card, Table, Tag, Switch, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { createAuthApi } from '@zero/auth-admin-ui';
import { useGridPageSize, GRID_PAGE_SIZE_OPTIONS } from '../../hooks/useGridPageSize.js';

const { Text } = Typography;

const SEVERITY_COLOR = { Info: 'blue', Warning: 'orange', Critical: 'red' };

export function AlertsPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useGridPageSize('alerts', 25);

  const refresh = () => {
    setLoading(true);
    api.listAlertRules()
      .then((r) => setRules(r.data?.Data ?? []))
      .catch((err) => message.error(err?.response?.data?.Message ?? err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const toggleActive = async (rule, isActive) => {
    try {
      await api.updateAlertRule(rule.AlertRuleID, { ...rule, IsActive: isActive });
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    }
  };

  const columns = [
    { title: t('common.id'), dataIndex: 'AlertRuleID', width: 70 },
    { title: t('common.name'), dataIndex: 'RuleName', render: (v) => <strong>{v}</strong> },
    { title: 'EventType', dataIndex: 'EventType', render: (v) => <Text code>{v}</Text> },
    { title: 'Threshold', dataIndex: 'Threshold', width: 100, align: 'right' },
    { title: 'Window', dataIndex: 'WindowMinutes', width: 100, align: 'right',
      render: (v) => `${v} min` },
    { title: 'Severity', dataIndex: 'Severity', width: 110,
      render: (v) => <Tag color={SEVERITY_COLOR[v] ?? 'default'}>{v}</Tag> },
    { title: 'Channel', dataIndex: 'NotifyChannel', width: 100,
      render: (v) => <Tag>{v}</Tag> },
    { title: 'Target', dataIndex: 'NotifyTarget', ellipsis: true,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: t('common.active'), dataIndex: 'IsActive', width: 100, align: 'center',
      render: (v, r) => <Switch checked={v} onChange={(c) => toggleActive(r, c)} /> },
  ];

  return (
    <Table
      dataSource={rules}
      columns={columns}
      loading={loading}
      rowKey="AlertRuleID"
      size="small"
      bordered
      pagination={{
        pageSize,
        showSizeChanger: true,
        pageSizeOptions: GRID_PAGE_SIZE_OPTIONS,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
      scroll={{ y: 'calc(100vh - 130px)' }}
    />
  );
}
